import { Link } from '@/i18n/routing';
import { SearchProductFacet } from '@winelivery-org/wl-client-sdk-js/src/models/search/facet';
import { upperFirst } from 'lodash';
import { useTranslations } from 'next-intl';
import ExternalLink from 'next/link';
import { Button } from '../../../../../../components/design-system';
import { ChevronRight } from '../../../../../../components/icons';
import FlyoutMenu from './FlyoutMenu';
import { getBanner } from './categoryBanners';
import Image from 'next/image';

interface Props {
  category: SearchProductFacet;
}

export default function FlyoutMenuCategory({ category }: Props) {
  const t = useTranslations('shop');
  const banner = getBanner(category.slug);

  return (
    <FlyoutMenu
      hiddenMenu={category.children.length < 2}
      label={upperFirst(category.name)}
      href={{
        pathname: '/shop',
        query: { category: category.slug },
      }}
    >
      <div className="flex gap-2 max-w-4xl">
        <div className="w-3/5 p-8">
          <div className="flex gap-4">
            <div>
              <div className="p-1 bg-greyscale-pink rounded-lg shrink-0">
                <div className="w-10 h-10 relative">
                  <Image
                    fill
                    src={category.iconUrl || ''}
                    alt={category.name}
                    className="object-cover shrink-0"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold tracking-tighter text-greyscale-title mb-1">
                {upperFirst(category.name)}
              </h4>
              <p className="text-base text-greyscale-label font-medium tracking-tight">
                {t(`navbar.category.${category.slug}.description`)}
              </p>
            </div>
          </div>
          <div className="flex gap-8 mt-4">
            <div className="space-y-2">
              <Link
                href={{ pathname: `/shop`, query: { category: category.slug } }}
                className="flex items-center gap-1 text-greyscale-body no-underline group/item"
              >
                <span className="text-base font-medium tracking-tight group-hover/item:underline">
                  {t(`navbar.category.${category.slug}.all`)}
                </span>
                <ChevronRight className="h-4 transition group-hover/item:translate-x-0.5" />
              </Link>
              {category.children
                .slice(0, Math.max(3, Math.floor(category.children.length / 2)))
                .map((subcategory) => (
                  <Link
                    key={subcategory.id}
                    href={{
                      pathname: `/shop`,
                      query: {
                        category: category.slug,
                        subcategory: subcategory.slug,
                      },
                    }}
                    className="flex items-center gap-1 text-greyscale-body no-underline group/item"
                  >
                    <>
                      <span className="text-base font-medium tracking-tight group-hover/item:underline">
                        {upperFirst(subcategory.name)}
                      </span>
                      <ChevronRight className="h-4 transition group-hover/item:translate-x-0.5" />
                    </>
                  </Link>
                ))}
            </div>
            <div className="space-y-2">
              {category.children
                .slice(Math.max(3, Math.floor(category.children.length / 2)))
                .map((subcategory) => (
                  <Link
                    key={subcategory.id}
                    href={{
                      pathname: `/shop`,
                      query: {
                        category: category.slug,
                        subcategory: subcategory.slug,
                      },
                    }}
                    className="flex items-center gap-1 text-greyscale-body no-underline group/item"
                  >
                    <>
                      <span className="text-base font-medium tracking-tight group-hover/item:underline">
                        {upperFirst(subcategory.name)}
                      </span>
                      <ChevronRight className="h-4 transition group-hover/item:translate-x-0.5" />
                    </>
                  </Link>
                ))}
            </div>
          </div>
        </div>
        {!!banner && (
          <div
            className="w-2/5 bg-[#f8f2f1] p-8 bg-contain bg-no-repeat bg-right-bottom"
            style={{ backgroundImage: `url('${banner.imageUrl}')` }}
          >
            <div className="w-2/3">
              <h4 className="text-xl font-bold tracking-tighter text-greyscale-title mb-1">
                {t(`navbar.category.${category.slug}.bannerTitle`)}
              </h4>
              <p className="text-base text-greyscale-label font-medium tracking-tight">
                {t(`navbar.category.${category.slug}.bannerDescription`)}
              </p>
              {banner.target ? (
                <ExternalLink href={banner.href} target="_blank">
                  <Button type="link" theme="outline">
                    {t(`navbar.category.${category.slug}.cta`)}
                  </Button>
                </ExternalLink>
              ) : (
                <Link
                  // @ts-expect-error
                  href={banner.href}>
                  <Button type="link" theme="outline">
                    {t(`navbar.category.${category.slug}.cta`)}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </FlyoutMenu>
  );
}
